import {
  Activity,
  ArrowRight,
  Boxes,
  ClipboardCheck,
  Clock3,
  Gauge,
  History,
  Network,
  ShieldCheck,
  Siren,
  UsersRound,
  Wrench,
} from "lucide-react";
import type { RoleDefinition } from "../../app/roles";
import { ImpactTrace } from "../../components/ImpactTrace";
import { StatusPill } from "../../components/StatusPill";
import { activeIncident, campusSummary, serviceHealth } from "../../data/seed";
import { DIVE_STAGE_DEFINITIONS, type DiveStage } from "../../domain";
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from "../../presentation";
import { useScenario } from "../../simulation/ScenarioContext";
import { selectDiveStage } from "../../simulation/guidedOperationsSelectors";
import { TelecomDashboard } from "./TelecomDashboard";
import { telecomSnapshot } from "./telecomPresentation";

type OperationsCenterProps = {
  role: RoleDefinition;
  onOpenIncident: () => void;
  onOpenTrace: () => void;
  onOpenAssets: () => void;
  onOpenMaintenance: () => void;
};

function healthTone(status: string): "healthy" | "warning" | "critical" | "neutral" {
  if (status === "healthy") return "healthy";
  if (status === "degraded") return "warning";
  if (status === "disrupted" || status === "offline") return "critical";
  return "neutral";
}

const operationsDepthContent = defineDepthContent({
  overview: {
    calmSummary: "Campus technology is stable. Review current health, attention items, and the next safe action.",
    incidentSummary: "One academic building has reduced network and phone reliability.",
    title: "What needs attention",
    signals: ["Current campus outcome", "People and services affected", "Next safe operational action"] as const,
  },
  operations: {
    calmSummary: "Trace campus technology health, understand operational impact, and act with evidence.",
    incidentSummary: "Use service health, evidence, ownership, and receipts to guide the active response.",
    title: "Operational response",
    signals: ["Service health and active work", "Evidence-backed ownership", "Actions with accountable receipts"] as const,
  },
  architecture: {
    calmSummary: "Inspect how service, infrastructure, asset, assignment, and activity records form the campus baseline.",
    incidentSummary: "Follow INC-2048 through CR-SW-04, NA-214-A1, TEL-0318, and the assigned user record.",
    title: "Connected record system",
    signals: ["Stable anchor identifiers", "Service-to-person dependencies", "Seed, repository, and simulation provenance"] as const,
  },
});

const diveStages: DiveStage[] = ["diagnose", "integrate", "validate", "enable"];

export function OperationsCenter({
  role,
  onOpenIncident,
  onOpenTrace,
  onOpenAssets,
  onOpenMaintenance,
}: OperationsCenterProps) {
  const {
    assignedOwner,
    linkedEvidenceIds,
    receipts,
    remainingUsers,
    resetScenario,
    selectedDecision,
    stage,
    startScenario,
  } = useScenario();
  const { depth } = useDepth();
  const lens = useDepthValue(operationsDepthContent);
  const scenarioActive = stage !== "calm";
  const recovered = stage === "recovered";
  const snapshot = telecomSnapshot(stage, selectedDecision?.id ?? null);
  const serviceAvailability = snapshot.serviceSla.availability;
  const currentOwner = assignedOwner?.name ?? activeIncident.owner;
  const activeDiveStage = selectDiveStage(stage);

  return (
    <section aria-labelledby="operations-title">
      <div className={scenarioActive && !recovered ? "operations-hero is-incident" : "operations-hero"}>
        <div className="operations-hero__content">
          <div>
            <p className="eyebrow"><ShieldCheck aria-hidden="true" size={14} /> Cedar Reach College · TeleCom Operations Center</p>
            <h1 id="operations-title">TeleCom Operations for Cedar Reach College</h1>
            <p className="operations-hero__tagline">Reduce downtime. Track every fix. Keep campus connected.</p>
            <p className="operations-hero__description">
              Simulate, track, and resolve campus network outages with live local visibility, clear ownership, and
              audit-ready logs—<strong>all in one place.</strong>
            </p>
          </div>
          <div className="heading-actions">
            {scenarioActive && (
              <button className="button button--secondary button--on-dark" type="button" onClick={resetScenario}>
                Reset to calm
              </button>
            )}
            <button
              className="button button--signal"
              type="button"
              onClick={() => recovered ? resetScenario() : scenarioActive ? onOpenIncident() : startScenario(role.label)}
            >
              <Siren aria-hidden="true" size={18} />
              {recovered ? "Run the Scenario Again" : scenarioActive ? "Continue Outage Response" : "Simulate a Campus Network Outage"}
              <ArrowRight aria-hidden="true" size={18} />
            </button>
          </div>
          <div className="operations-hero__trust">
            <span><span className="live-dot" aria-hidden="true" /> Local demonstration data</span>
            <span><Network aria-hidden="true" size={15} /> Wi-Fi · VoIP · switching</span>
            <span><Clock3 aria-hidden="true" size={15} /> Traceable response receipts</span>
            <span className="operations-hero__learning-trail">Signal → relationship → evidence → recovery</span>
          </div>
        </div>
        <div className="operations-hero__visual" aria-hidden="true">
          <img src="/brand/campus-fieldbook.webp" alt="" />
          <div className="hero-signal-card">
            <span>Day in the Life · finals week</span>
            <strong>{scenarioActive && !recovered ? "INC-2048 active" : "North Academic outage story"}</strong>
            <small>One deterministic scenario from first alert to verified recovery</small>
          </div>
        </div>
      </div>

      <TelecomDashboard
        decisionId={selectedDecision?.id ?? null}
        linkedEvidenceCount={linkedEvidenceIds.length}
        onOpenIncident={onOpenIncident}
        onOpenTrace={onOpenTrace}
        receipts={receipts}
        showTools
        stage={stage}
      />

      <section className="dive-journey-banner" aria-labelledby="dive-journey-title">
        <header>
          <div>
            <p className="eyebrow">DIVE framework</p>
            <h2 id="dive-journey-title">One outage response, four accountable stages</h2>
          </div>
          <span>Current stage · {DIVE_STAGE_DEFINITIONS[activeDiveStage].label}</span>
        </header>
        <ol>
          {diveStages.map((diveStage, index) => {
            const definition = DIVE_STAGE_DEFINITIONS[diveStage];
            const isActive = diveStage === activeDiveStage;
            return (
              <li className={isActive ? "is-active" : ""} key={diveStage}>
                <span>{index + 1}</span>
                <div>
                  <strong>{definition.label}</strong>
                  <p>{definition.objective}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="why-matters" aria-labelledby="why-matters-title">
        <header>
          <p className="eyebrow">Why this matters</p>
          <h2 id="why-matters-title">A calmer, more accountable campus response</h2>
          <p>Every view connects the service problem to the people, evidence, owner, and next safe action.</p>
        </header>
        <div className="why-matters__grid">
          <article>
            <Activity aria-hidden="true" />
            <strong>Real-Time Visibility</strong>
            <p>See deterministic Wi-Fi, voice, switch, and service changes together as the local scenario advances.</p>
          </article>
          <article>
            <UsersRound aria-hidden="true" />
            <strong>Clear Ownership</strong>
            <p>Keep the accountable operator and response owner visible from acknowledgement through recovery.</p>
          </article>
          <article>
            <ClipboardCheck aria-hidden="true" />
            <strong>Audit-Ready Logs</strong>
            <p>Preserve evidence, overrides, decisions, verification, and handoff work as append-only receipts.</p>
          </article>
          <article>
            <History aria-hidden="true" />
            <strong>Faster Response Times</strong>
            <p>Move from alert to the next safe action without searching across disconnected tools and timelines.</p>
          </article>
        </div>
      </section>

      <DepthBrief summary={scenarioActive ? lens.incidentSummary : lens.calmSummary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="operations" />

      {depth === "overview" && <div className="command-strip" aria-label="Operational summaries">
        <button onClick={onOpenTrace} type="button">
          <span className="command-strip__icon"><Gauge aria-hidden="true" size={19} /></span>
          <span>Service availability</span><strong>{serviceAvailability.value}{serviceAvailability.unit}</strong>
          <small>Open impact trace <ArrowRight aria-hidden="true" size={15} /></small>
        </button>
        <button onClick={onOpenIncident} type="button">
          <span className="command-strip__icon"><Siren aria-hidden="true" size={19} /></span>
          <span>Active incidents</span><strong>{scenarioActive ? campusSummary.activeIncidents : 1}</strong>
          <small>Open incident queue <ArrowRight aria-hidden="true" size={15} /></small>
        </button>
        <button onClick={onOpenAssets} type="button">
          <span className="command-strip__icon"><Boxes aria-hidden="true" size={19} /></span>
          <span>Assets requiring attention</span><strong>{campusSummary.assetsRequiringAttention}</strong>
          <small>Open asset view <ArrowRight aria-hidden="true" size={15} /></small>
        </button>
        <button onClick={onOpenMaintenance} type="button">
          <span className="command-strip__icon"><Wrench aria-hidden="true" size={19} /></span>
          <span>Maintenance due today</span><strong>{campusSummary.maintenanceDueToday}</strong>
          <small>Open maintenance <ArrowRight aria-hidden="true" size={15} /></small>
        </button>
      </div>}

      {scenarioActive && (
        <article className={recovered ? "scenario-banner scenario-banner--recovered" : "scenario-banner"} aria-live="polite">
          <div>
            <span>{recovered ? "Recovery verified" : "Controlled incident active"}</span>
            <strong>{selectedDecision ? selectedDecision.label : "Evidence collection in progress"}</strong>
          </div>
          <div>
            <span>{receipts.length} receipts</span>
            <button onClick={onOpenIncident} type="button">{recovered ? "Review closure" : "Continue investigation"} →</button>
          </div>
        </article>
      )}

      {depth === "operations" && <div className="operations-grid">
        <article className="panel panel--services">
          <header className="panel__header">
            <div><p className="eyebrow">Service health</p><h2>Campus services</h2></div>
            <span>Deterministic check · {snapshot.switchHealth.lastCheck.value}</span>
          </header>
          <div className="service-list">
            {serviceHealth.map((service) => {
              const status = scenarioActive && !recovered ? service.status : "healthy";
              return (
                <button className="service-row" key={service.id} onClick={onOpenTrace} type="button">
                  <div>
                    <strong>{service.name}</strong>
                    <span>
                      {scenarioActive && !recovered && service.affectedBuildings.length
                        ? service.affectedBuildings.join(", ")
                        : "No buildings affected"}
                    </span>
                  </div>
                  <div className="service-row__status">
                    <StatusPill label={status} tone={healthTone(status)} />
                    <span>{scenarioActive && !recovered ? service.availability : 99.9}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </article>

        <article className={`panel incident-card ${scenarioActive && !recovered ? "incident-card--active" : ""}`}>
          <header className="panel__header">
            <div>
              <p className="eyebrow">{recovered ? "Recently recovered" : "Highest priority"}</p>
              <h2>{scenarioActive ? activeIncident.id : "No major incident"}</h2>
            </div>
            <StatusPill
              label={scenarioActive ? recovered ? "Recovered" : activeIncident.severity : "Calm"}
              tone={scenarioActive && !recovered ? "critical" : "healthy"}
            />
          </header>
          {scenarioActive ? (
            <>
              <h3>{activeIncident.title}</h3>
              <p>{remainingUsers} estimated users affected · Owner: {currentOwner}</p>
              <dl className="incident-facts">
                <div><dt>Status</dt><dd>{stage}</dd></div>
                <div><dt>Building</dt><dd>{activeIncident.affectedBuildings.join(", ")}</dd></div>
                <div><dt>Working theory</dt><dd>{activeIncident.workingTheory}</dd></div>
              </dl>
              <button className="button button--secondary" type="button" onClick={onOpenIncident}>
                Open incident workspace
              </button>
            </>
          ) : (
            <p>Routine operations are stable. Start the controlled outage scenario to activate the investigation path.</p>
          )}
        </article>

        {scenarioActive && (
          <article className="panel operations-trace">
            <header className="panel__header">
              <div><p className="eyebrow">Scenario relationship path</p><h2>Impact propagation</h2></div>
              <button className="text-button" onClick={onOpenTrace} type="button">Expand</button>
            </header>
            <ImpactTrace compact />
          </article>
        )}
      </div>}

      {depth === "architecture" && (
        <section className="depth-surface architecture-path-panel" aria-label="Architecture record map">
          <header>
            <div>
              <p className="eyebrow">Record relationship map</p>
              <h2>Service state resolves through explicit records</h2>
            </div>
            <button className="text-button" onClick={onOpenTrace} type="button">Open full topology</button>
          </header>
          <ImpactTrace />
          <div className="architecture-record-grid">
            <article className="architecture-record-card">
              <span>Operational root</span>
              <strong>{scenarioActive ? "INC-2048" : "Campus service baseline"}</strong>
              <p>{scenarioActive ? "Correlates CHG-9012 with service, device, and person impact." : "Service health records are joined to buildings and accountable teams."}</p>
            </article>
            <article className="architecture-record-card">
              <span>Infrastructure anchors</span>
              <strong>CR-SW-04 → NA-214-A1</strong>
              <p>Typed device, interface, VLAN, patch-panel, and jack relationships preserve directionality.</p>
            </article>
            <article className="architecture-record-card">
              <span>Asset and person edge</span>
              <strong>TEL-0318 → USR-PRIYA-NAIR</strong>
              <p>Assignment ASN-1182 connects the endpoint impact to the permitted person record.</p>
            </article>
          </div>
        </section>
      )}
    </section>
  );
}
